import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CONSULT_AREAS } from "@/lib/constants";
import api, { formatApiError } from "@/lib/api";

const CONTACT_METHODS = ["Phone call", "WhatsApp", "Email", "Video call", "In person"];
const STEP_LABELS = ["Service area", "Your details", "Your circumstances", "Contact preference", "Confirm"];

export default function BookConsultation() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    type: "consultation", service_area: "", name: "", email: "", phone: "",
    location: "", summary: "", preferred_contact: "", preferred_time: "",
    consent: false, company: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 1) return !!form.service_area;
    if (step === 2) return form.name.trim() && /\S+@\S+\.\S+/.test(form.email);
    if (step === 3) return form.summary.trim().length > 3;
    if (step === 4) return !!form.preferred_contact;
    return true;
  };

  const submit = async () => {
    if (!form.consent) { toast.error("Please accept the privacy notice to continue."); return; }
    setSubmitting(true);
    try {
      await api.post("/public/enquiries", form);
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <>
        <Seo title="Consultation Requested | ApexPath" />
        <div className="container-x flex min-h-[70vh] items-center justify-center py-20">
          <div className="max-w-lg rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="consultation-confirmation">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-gold">
              <Check size={34} />
            </div>
            <h1 className="font-serif text-3xl font-bold text-navy">Thank You</h1>
            <p className="mt-3 text-base leading-relaxed text-slate-600">
              Your consultation request has been received. A member of the ApexPath team will contact you using your
              preferred method to discuss the next step. We look forward to helping you.
            </p>
            <button onClick={() => navigate("/")} className="btn-gold mt-7">Back to Home</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo title="Book a Consultation | ApexPath"
        description="Book a confidential immigration consultation with ApexPath. Simple, mobile-friendly and reassuring." />
      <PageHero
        crumbs={[{ label: "Home", to: "/" }, { label: "Book a Consultation" }]}
        eyebrow="Book a Consultation"
        title="Let's Discuss" highlight="Your Situation"
        image="/images/consult.jpg"
        subtitle="Tell us a little about what you need and we will get in touch. It is simple, confidential and there is no obligation." />

      <section className="bg-cream py-16">
        <div className="container-x max-w-3xl">
          {/* Stepper */}
          <div className="mb-8 flex items-center justify-between">
            {STEP_LABELS.map((label, i) => {
              const n = i + 1;
              return (
                <div key={label} className="flex flex-1 items-center">
                  <div className="flex flex-col items-center">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                      n < step ? "bg-gold text-navy" : n === step ? "bg-navy text-white" : "bg-slate-200 text-slate-500"
                    }`}>
                      {n < step ? <Check size={16} /> : n}
                    </div>
                    <span className="mt-1.5 hidden text-[11px] text-slate-500 sm:block">{label}</span>
                  </div>
                  {n < STEP_LABELS.length && <div className={`mx-1 h-0.5 flex-1 ${n < step ? "bg-gold" : "bg-slate-200"}`} />}
                </div>
              );
            })}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-9">
            {/* Step 1 */}
            {step === 1 && (
              <div data-testid="consult-step-1">
                <h2 className="font-serif text-2xl font-semibold text-navy">Which area do you need help with?</h2>
                <p className="mt-1 text-sm text-slate-500">Choose the option closest to your situation.</p>
                <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {CONSULT_AREAS.map((a) => (
                    <button key={a} onClick={() => set("service_area", a)}
                      data-testid={`consult-area-${a.slice(0, 12).replace(/\W/g, "-")}`}
                      className={`rounded-lg border p-4 text-left text-sm font-medium transition-colors ${
                        form.service_area === a ? "border-gold bg-gold/10 text-navy" : "border-slate-200 text-slate-600 hover:border-navy"
                      }`}>
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div data-testid="consult-step-2" className="space-y-4">
                <h2 className="font-serif text-2xl font-semibold text-navy">Your contact details</h2>
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" data-testid="consult-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" placeholder="Your name" />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="email">Email address</Label>
                    <Input id="email" type="email" data-testid="consult-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" placeholder="you@example.com" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone / WhatsApp</Label>
                    <Input id="phone" data-testid="consult-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5" placeholder="Optional" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="location">Where are you currently based?</Label>
                  <Input id="location" data-testid="consult-location" value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5" placeholder="e.g. Inside the UK / Overseas (optional)" />
                </div>
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <div data-testid="consult-step-3">
                <h2 className="font-serif text-2xl font-semibold text-navy">Tell us about your circumstances</h2>
                <p className="mt-1 text-sm text-slate-500">A short summary is enough — please do not upload passports or sensitive documents at this stage.</p>
                <Textarea data-testid="consult-summary" value={form.summary} onChange={(e) => set("summary", e.target.value)}
                  className="mt-4 min-h-[160px]" placeholder="Briefly describe your situation and what you would like help with…" />
              </div>
            )}

            {/* Step 4 */}
            {step === 4 && (
              <div data-testid="consult-step-4" className="space-y-5">
                <div>
                  <h2 className="font-serif text-2xl font-semibold text-navy">How would you like us to contact you?</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {CONTACT_METHODS.map((m) => (
                      <button key={m} onClick={() => set("preferred_contact", m)}
                        data-testid={`consult-contact-${m.split(" ")[0].toLowerCase()}`}
                        className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                          form.preferred_contact === m ? "border-navy bg-navy text-white" : "border-slate-300 text-slate-600 hover:border-navy"
                        }`}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="time">Preferred date / time (optional)</Label>
                  <Input id="time" data-testid="consult-time" value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)} className="mt-1.5" placeholder="e.g. Weekday mornings" />
                </div>
              </div>
            )}

            {/* Step 5 */}
            {step === 5 && (
              <div data-testid="consult-step-5">
                <h2 className="font-serif text-2xl font-semibold text-navy">Review & submit</h2>
                <dl className="mt-5 divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {[
                    ["Service area", form.service_area], ["Name", form.name], ["Email", form.email],
                    ["Phone", form.phone || "—"], ["Location", form.location || "—"],
                    ["Preferred contact", form.preferred_contact], ["Preferred time", form.preferred_time || "—"],
                    ["Summary", form.summary],
                  ].map(([k, v]) => (
                    <div key={k} className="flex gap-4 px-4 py-3 text-sm">
                      <dt className="w-36 shrink-0 text-slate-500">{k}</dt>
                      <dd className="font-medium text-navy">{v}</dd>
                    </div>
                  ))}
                </dl>
                {/* Honeypot */}
                <input type="text" value={form.company} onChange={(e) => set("company", e.target.value)}
                  tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
                <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-slate-600">
                  <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="consult-consent" className="mt-0.5" />
                  <span>I agree to ApexPath handling my information in line with the <a href="/legal/privacy" className="text-gold underline">privacy notice</a> in order to respond to my enquiry.</span>
                </label>
              </div>
            )}

            {/* Nav */}
            <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-6">
              <button onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
                data-testid="consultation-wizard-back-btn"
                className={`flex items-center gap-1.5 text-sm font-medium ${step === 1 ? "invisible" : "text-navy hover:text-gold"}`}>
                <ArrowLeft size={15} /> Back
              </button>
              {step < 5 ? (
                <button onClick={() => canNext() ? setStep((s) => s + 1) : toast.error("Please complete this step to continue.")}
                  data-testid="consultation-wizard-next-btn" className="btn-gold text-sm">
                  Continue <ArrowRight size={15} />
                </button>
              ) : (
                <button onClick={submit} disabled={submitting}
                  data-testid="consultation-wizard-submit-btn" className="btn-gold text-sm disabled:opacity-60">
                  {submitting ? "Submitting…" : "Submit Enquiry"} <ArrowRight size={15} />
                </button>
              )}
            </div>
          </div>

          <p className="mt-5 flex items-center justify-center gap-2 text-xs text-slate-500">
            <ShieldCheck size={14} className="text-gold" /> Your information is kept confidential and secure.
          </p>
        </div>
      </section>
    </>
  );
}
